/**
 * ExoClick's JS snippet (`<ins data-zoneid>` + ad-provider.js) is written to
 * run directly in the publisher's own page. Every ad iframe on this site
 * instead embeds that snippet via `srcDoc` inside `sandbox="allow-scripts"`
 * (no `allow-same-origin`) to contain a real incident where the script,
 * run unsandboxed, entered a broken retry loop and stole clicks meant for
 * our own UI (see NetworkDisplayAd's history).
 *
 * That containment has a side effect that went unnoticed: without
 * `allow-same-origin` the frame gets an opaque origin, and ad-provider.js's
 * first move is `document.cookie = ...`, which throws immediately in an
 * opaque-origin document ("Failed to set the 'cookie' property on
 * 'Document': The document is sandboxed and lacks the 'allow-same-origin'
 * flag" — reproduced live). The ad request still fires and ExoClick still
 * logs a fill, but the creative is built into a detached div that never
 * lands inside the visible `<ins>` — the slot renders as a permanently
 * empty box. That's the "ads just show a white box" bug.
 *
 * Adding `allow-same-origin` to the existing `srcDoc` iframe is not a safe
 * fix: a sandboxed `srcDoc` document with `allow-same-origin` is defined by
 * spec to inherit its CREATOR's origin (ours), which restores full DOM
 * access to our real page — the exact bug the sandbox exists to prevent.
 *
 * The actual fix: ExoClick also serves each zone from a genuine
 * cross-origin endpoint (`a.magsrv.com/iframe.php?idzone=...`). Pointing a
 * real `src=` (not `srcDoc`) at that URL with
 * `sandbox="allow-scripts allow-same-origin"` gives the frame magsrv.com's
 * own real origin, so its cookie writes succeed and the creative renders —
 * while ordinary cross-origin browsing rules (not sandbox flags) keep it
 * from ever reaching our page, regardless of `allow-same-origin`. Verified
 * live: a real creative rendered, and a deliberate attempt from inside the
 * frame to read `window.parent.document` was blocked by the browser with
 * "Blocked a frame with origin ... from accessing a cross-origin frame."
 *
 * Falls back to the old srcDoc+allow-scripts-only rendering for embed code
 * that isn't a recognized ExoClick zone tag, since we don't know a safe
 * cross-origin URL for other networks.
 */
export function extractExoclickZoneId(embedHtml: string | null | undefined): string | null {
  if (!embedHtml) return null
  const m = embedHtml.match(/data-zoneid=["'](\d+)["']/)
  return m ? m[1] : null
}

export function exoclickIframeSrc(zoneId: string): string {
  return `https://a.magsrv.com/iframe.php?idzone=${zoneId}`
}
